import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
  HttpErrorResponse,
  HttpContextToken,
} from '@angular/common/http';
import { Observable, EMPTY, throwError } from 'rxjs';
import { SessionService } from '@features/session/session.service';
import { AlertService } from '@core/services/alert.service';
import { Router } from '@angular/router';
import { catchError, tap } from 'rxjs/operators';

export interface HttpError {
  error: string;
  message: string;
  path: string;
  status: string;
  timestamp: string;
}

export const SUPPRESS_HTTP_ERROR_ALERT = new HttpContextToken<boolean>(
  () => false
);

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(
    private session: SessionService,
    private alert: AlertService,
    private router: Router
  ) {}

  /** Convenience accessor to keep the template tidy. */
  private get accessToken(): string {
    return (
      this.session.currentUser?.signInUserSession?.accessToken?.jwtToken ?? ''
    );
  }

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      /**
       * ▸ If your API occasionally returns HTTP 200 with `{ success:false }`
       *   you can inspect/throw it here in `tap`.
       */
      tap((evt) => {
        if (evt instanceof HttpResponse) {
          const body: any = evt.body;
          if (body?.success === false) {
            /** Business-logic failure disguised as 200 OK */
            throw new HttpErrorResponse({
              status: 422,
              statusText: 'Business Error',
              url: evt.url ?? undefined,
              error: { errorMessage: body.errorMessage ?? 'Unknown error' },
            });
          }
        }
      }),

      /**
       * ▸ Network / real HTTP errors.
       * ▸ Guard every property access – never assume the backend shape.
       */
      catchError((err: HttpErrorResponse) => {
        /** ---------- 401 – session expired ---------- */
        if (err.status === 401) {
          this.alert.showAlert(
            'Session expired. Please log in again.',
            'error'
          );
          localStorage.clear();
          this.session.navigateToLoginPage(); // keeps the intent clear
          return EMPTY; // stop the stream cold
        }

        if (err.status === 423) {
          this.alert.showAlert(
            'Your organisation is inactive. Please contact your school administrator.',
            'error'
          );
          void this.session.logoutQuietly();
          this.session.navigateToLoginPage();
          return throwError(() => err);
        }

        /** ---------- Every other HTTP error ---------- */
        const serverMsg =
          (err.error as any)?.errorMessage ??
          (err.error as any)?.message ??
          err.message ??
          'Something went wrong, please try again later.';

        if (!req.context.get(SUPPRESS_HTTP_ERROR_ALERT)) {
          this.alert.showAlert(serverMsg, 'error');
        }
        // Re-throw the *whole* HttpErrorResponse so callers can decide.
        return throwError(() => err);
      })
    );
  }
}
